/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { css, jsx } from '@compiled/react';
import { useYbrIsNewUser } from '@jira/yellow-brick-road-utils/src/controllers/useYbrIsNewUser.tsx';
import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button/new';
import FilterIcon from '@atlaskit/icon/core/filter';
import type { ContentProps, TriggerProps } from '@atlaskit/popup';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { SpotlightManager, SpotlightPulse } from '@atlaskit/onboarding';
import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import type { ExternalMessage } from '@atlaskit/jql-editor';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import {
	fireUIAnalytics,
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { ChoreographerContextProvider } from '@atlassian/jira-choreographer-services/src/ChoreographerContextProvider.tsx';
import { CoordinationClient } from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { PACKAGE_NAME, TEAM_NAME, type Tabs } from '../../common/constants.tsx';
import { useJQL } from '../../controllers/ast/index.tsx';
import { useNumberOfSelectedValues } from '../../controllers/ast/use-selected-values/index.tsx';
import { SelectedFieldContainer } from '../../controllers/use-selected-field/index.tsx';
import type { ChangeBoardingView } from '../../common/types.tsx';
import type { JiraJqlScopeInput } from './filter-popup-content/ui/__generated__/FilterPopupContentQuery.graphql';
import { ChangeBoarding } from './change-boarding/index.tsx';
import { FilterPopupContainer } from './filter-popup-container/index.tsx';
import { filterPopupContentEntryPoint } from './filter-popup-content/entrypoint.tsx';
import {
	ErrorMessage,
	ErrorMessageWrapper,
} from './filter-popup-content/ui/error-message/index.tsx';
import { FallbackSkeleton as BasicFallbackSkeleton } from './filter-popup-content/ui/basic/fallback-skeleton/index.tsx';
import { FilterPopupTrigger } from './filter-popup-trigger/index.tsx';
import { FallbackSkeleton } from './filter-popup-content/ui/fallback-skeleton/index.tsx';
import messages from './filter-popup-trigger/messages.tsx';
import type { PopupHeight, PopupWidth } from './types.tsx';
import KeyboardShortcuts from './keyboard-shortcuts/index.tsx';

const POPUP_HEIGHT: PopupHeight = '380px';
const POPUP_WIDTH: PopupWidth = '600px';

type Props = {
	scope?: JiraJqlScopeInput;
	changeboardingMessageId?: string;
	enablePostOfficeTour?: boolean;
	changeBoardingView?: ChangeBoardingView;
	isSoftwareFullTheming?: boolean;
	isModeSwitcherEnabled?: boolean;
	projectContext?: string;
	onReset?: () => void;
	jqlMessages?: ExternalMessage[];
	isSearching?: boolean;
};

export const FilterPopup = ({
	scope,
	changeboardingMessageId,
	enablePostOfficeTour,
	changeBoardingView,
	isSoftwareFullTheming,
	isModeSwitcherEnabled = false,
	projectContext,
	onReset,
	jqlMessages,
	isSearching,
}: Props) => {
	const jql = useJQL();
	const numberOfSelectedValues = useNumberOfSelectedValues(
		fg('list_lovability_improving_filters') ? projectContext : undefined,
	);
	const { formatMessage } = useIntl();
	const [isFeedbackOpen, setFeedbackIsOpen] = useState(false);

	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [activeTab, setActiveTab] = useState<Tabs | undefined>();

	const cloudId = useCloudId();
	const { data: orgId } = useOrgId();

	// we don't want to fetch data and cause rerender when we know user won't see change boarding
	const [isYbrNewUser] = useYbrIsNewUser(
		Boolean(enablePostOfficeTour || changeboardingMessageId === undefined),
	);

	const coordinationClient = useMemo(
		() =>
			changeboardingMessageId
				? new CoordinationClient(cloudId, '/gateway/api', false, orgId, 'engagement')
				: undefined,
		[changeboardingMessageId, cloudId, orgId],
	);
	const [activeSpotlight, setActiveSpotlight] = useState<null | number>(null);
	const startSpotlight = useCallback(
		(isActive: boolean) => setActiveSpotlight(isActive ? 0 : null),
		[],
	);
	const nextSpotlight = useCallback(
		() => setActiveSpotlight((activeSpotlight || 0) + 1),
		[activeSpotlight],
	);
	const endSpotlight = useCallback(() => setActiveSpotlight(null), []);

	useEffect(() => {
		if (changeboardingMessageId && !isYbrNewUser) {
			coordinationClient
				?.start(changeboardingMessageId)
				.then((isActive) => startSpotlight(isActive))
				.catch(() => endSpotlight());
		}
	}, [coordinationClient, changeboardingMessageId, startSpotlight, endSpotlight, isYbrNewUser]);

	const closeChangeboarding = useCallback(
		(event: React.MouseEvent<Element, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			// Stop propagation avoid closing filter popup when dismissing changeboarding
			event.stopPropagation();

			if (changeboardingMessageId) {
				coordinationClient?.stop(changeboardingMessageId);
				endSpotlight();
				fireUIAnalytics(analyticsEvent, 'filterRefinementChangeboardingClosed');
			}
		},
		[coordinationClient, changeboardingMessageId, endSpotlight],
	);

	const runtimeProps = useMemo(
		() => ({
			onCloseChangeboarding: closeChangeboarding,
			activeSpotlight,
			changeboardingMessageId,
			onTriggerNextSpotlight: nextSpotlight,
			onTriggerEndSpotlight: endSpotlight,
			projectContext,
			onActiveTabChange: setActiveTab,
			jqlMessages,
			isSearching,
		}),
		[
			closeChangeboarding,
			activeSpotlight,
			changeboardingMessageId,
			nextSpotlight,
			endSpotlight,
			projectContext,
			jqlMessages,
			isSearching,
		],
	);

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			jqlContext: jql,
			scope,
			onFeedbackClick: setFeedbackIsOpen,
			isModeSwitcherEnabled: fg('list_lovability_improving_filters')
				? isModeSwitcherEnabled
				: false,
			onReset,
		}),
		[cloudId, isModeSwitcherEnabled, jql, onReset, scope],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		filterPopupContentEntryPoint,
		entryPointParams,
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	const onClick = useCallback(
		(_: React.MouseEvent<HTMLButtonElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			setIsOpen((open) => !open);
			fireUIAnalytics(analyticsEvent, 'filter');
		},
		[],
	);

	const onKeyboardShortcutTriggered = useCallback(() => {
		entryPointActions.load();
		setIsOpen((open) => !open);
		fireUIAnalytics(
			createAnalyticsEvent({ action: 'pressed', actionSubject: 'keyboardShortcut' }),
			'filterPopup',
		);
	}, [createAnalyticsEvent, entryPointActions]);

	const onClose = useCallback(() => {
		// don't close the popup when the feedback modal is still open
		!isFeedbackOpen && setIsOpen(false);
	}, [isFeedbackOpen]);

	const formattedTooltipContent = useMemo(() => {
		return (
			<Box xcss={tooltipContentStyles}>
				<FormattedMessage
					{...messages.tooltipMessage}
					values={{
						inline: (parts: React.ReactNode[]) => (
							<Inline xcss={keyboardShortcutTagStyles}>{parts}</Inline>
						),
					}}
				/>
			</Box>
		);
	}, []);

	const hasErrors = useMemo(() => {
		if (!fg('list_lovability_improving_filters')) return false;

		if (jqlMessages && jqlMessages?.length > 0) {
			const idx = jqlMessages.findIndex((item) => item.type === 'error');

			if (idx > -1) {
				return true;
			}
		}
		return false;
	}, [jqlMessages]);

	const trigger = useCallback(
		(triggerProps: TriggerProps) => (
			<ChangeBoarding isEnabled={enablePostOfficeTour} changeBoardingView={changeBoardingView}>
				{fg('jira_jif_jam_filter_refinement_responsive') ? (
					<FilterPopupTrigger
						hasPulse={activeSpotlight !== null && activeSpotlight !== 1}
						isSelected={isOpen || numberOfSelectedValues > 0}
						isSoftwareFullTheming={isSoftwareFullTheming}
						onClick={onClick}
						ref={triggerRef}
						triggerProps={triggerProps}
						projectContext={fg('list_lovability_improving_filters') ? projectContext : undefined}
						hasErrors={fg('list_lovability_improving_filters') ? hasErrors : undefined}
					/>
				) : (
					<SpotlightPulse radius={3} pulse={activeSpotlight !== null && activeSpotlight !== 1}>
						<Tooltip content={formattedTooltipContent} hideTooltipOnMouseDown>
							{(tooltipProps) => (
								<Button
									interactionName="filter-popup-load-content"
									{...tooltipProps}
									{...triggerProps}
									iconBefore={FilterIcon}
									iconAfter={() =>
										numberOfSelectedValues > 0 && (
											<Box as="span" paddingInlineEnd="space.050" paddingInlineStart="space.025">
												{isSoftwareFullTheming ? (
													<div
														css={themedBadgeStyles}
														data-testid="filter-refinement.ui.filter-popup.badge"
													>
														{numberOfSelectedValues}
													</div>
												) : (
													<Badge
														appearance="primary"
														testId="filter-refinement.ui.filter-popup.badge"
													>
														{numberOfSelectedValues}
													</Badge>
												)}
											</Box>
										)
									}
									isSelected={isOpen || numberOfSelectedValues > 0}
									ref={mergeRefs(triggerRef, triggerProps.ref)}
									onClick={onClick}
									testId="filter-refinement.ui.filter-popup.button"
								>
									{formatMessage(messages.filterButton)}
								</Button>
							)}
						</Tooltip>
					</SpotlightPulse>
				)}
			</ChangeBoarding>
		),
		[
			enablePostOfficeTour,
			changeBoardingView,
			activeSpotlight,
			isOpen,
			numberOfSelectedValues,
			isSoftwareFullTheming,
			onClick,
			triggerRef,
			projectContext,
			hasErrors,
			formattedTooltipContent,
			formatMessage,
		],
	);

	const clickRetry = useCallback(async () => {
		// opening and closing the popup to trigger the content to re-render if the retry is successful
		// otherwise content will remain showing errorFallback
		setIsOpen(false);
		entryPointActions.unload();
		await entryPointActions.load();
		setIsOpen(true);
	}, [entryPointActions]);

	const wrapWithPopupContainer =
		fg('list_lovability_improving_filters') || fg('filter_refinement_m2_date_fields');

	const content = useCallback(
		({ update }: ContentProps) =>
			wrapWithPopupContainer ? (
				<FilterPopupContainer
					isModeSwitcherEnabled={isModeSwitcherEnabled}
					activeTab={activeTab}
					updatePopup={update}
				>
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="jira-filter-refinement-filter-popup-content"
						packageName={PACKAGE_NAME}
						teamName={TEAM_NAME}
						errorFallback={() => (
							<ErrorMessageWrapper isModeSwitcherEnabled={isModeSwitcherEnabled}>
								<ErrorMessage onClick={() => clickRetry()} errorType="fields" />
							</ErrorMessageWrapper>
						)}
						fallback={
							isModeSwitcherEnabled && fg('list_lovability_improving_filters') ? (
								<FallbackSkeleton />
							) : (
								<BasicFallbackSkeleton />
							)
						}
						runtimeProps={runtimeProps}
					/>
				</FilterPopupContainer>
			) : (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="jira-filter-refinement-filter-popup-content"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					errorFallback={() => (
						<Box xcss={containerStyles}>
							<ErrorMessage onClick={() => clickRetry()} errorType="fields" />
						</Box>
					)}
					fallback={<BasicFallbackSkeleton />}
					runtimeProps={runtimeProps}
				/>
			),
		[
			clickRetry,
			entryPointReferenceSubject,
			isModeSwitcherEnabled,
			runtimeProps,
			wrapWithPopupContainer,
			activeTab,
		],
	);

	return (
		<SelectedFieldContainer>
			<ChoreographerContextProvider
				isChoreographed={coordinationClient?.enableChoreographer ?? false}
			>
				<MaybeSpotlightManager blanketIsTinted={false}>
					<KeyboardShortcuts onShortcutTriggered={onKeyboardShortcutTriggered} />
					<Popup
						isOpen={isOpen}
						onClose={onClose}
						trigger={trigger}
						content={content}
						placement="bottom-start"
						// Why shouldRenderToParent=true here -> https://hello.atlassian.net/wiki/spaces/~712020136f113e259c452ea35e15a46d77f771/pages/6061276303/LDR+Filter+Refinement+on+Smaller+Screen+Sizes
						shouldRenderToParent={fg('list_lovability_improving_filters')}
						testId="filter-refinement.ui.filter-popup"
						messageId="filter-refinement.ui.filter-popup.popup"
						messageType="transactional"
						// shouldUseCaptureOnOutsideClick allow popup to close by triggering onClose function
						// when clicking an atlaskit dropdown (known bug)
						// https://atlassian.slack.com/archives/CFJ9DU39U/p1704237711295599?thread_ts=1703624547.647969&cid=CFJ9DU39U
						shouldUseCaptureOnOutsideClick={
							!expVal('filter_refinement_in_tmp_backlog', 'isEnabled', false)
						}
					/>
				</MaybeSpotlightManager>
			</ChoreographerContextProvider>
		</SelectedFieldContainer>
	);
};

const MaybeSpotlightManager = componentWithCondition(
	() =>
		expVal('filter_refinement_in_tmp_backlog', 'isEnabled', false) ||
		fg('changeboarding-filter-refinement-in-nin'),
	SpotlightManager,
	({ children }: PropsWithChildren<{ blanketIsTinted: boolean }>) => children,
);

const containerStyles = xcss({
	height: POPUP_HEIGHT,
	width: POPUP_WIDTH,
	color: 'color.text.subtle',
});

const keyboardShortcutTagStyles = xcss({
	display: 'inline-flex',
	height: '16px',
	padding: 'space.050',
	justifyContent: 'center',
	alignItems: 'center',
	gap: 'space.075',
	color: 'color.text.inverse',
	borderColor: 'color.border.inverse',
	borderRadius: 'radius.small',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	textAlign: 'center',
});

const tooltipContentStyles = xcss({
	textAlign: 'center',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	columnGap: 'space.050',
	paddingInline: 'space.075',
	paddingBlock: 'space.025',
});

const themedBadgeStyles = css({
	borderRadius: token('radius.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundColor: Tokens.COLOR_BACKGROUND_ACCENT_SELECTED,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	color: Tokens.COLOR_TEXT_ACCENT_SELECTED,
	paddingInline: token('space.050'),
	minWidth: token('space.200'),
	paddingLeft: token('space.050'),
	paddingRight: token('space.050'),
	textAlign: 'center',
	font: token('font.body.small'),
	display: 'inline-flex',
	justifyContent: 'center',
	blockSize: 'min-content',
});
